/**
 * Path search (BFS) over the board graph defined by walls.
 *
 * Per classic Quoridor rules, path-existence checks for wall legality
 * consider only walls — pawns do not block the path (a pawn can always
 * be jumped or will eventually move). The same convention applies to the
 * Phase 2 extensions: ice slides ignore pawns here too.
 *
 * Phase 2 context (`ctx`, optional — omit/null for classic behavior):
 *   ctx.wallJumps  : number of wall crossings the mover may still spend
 *                    (ninja's remaining wallJump uses). The search state is
 *                    (cell, jumpsLeft); crossing a walled edge costs 1.
 *   ctx.gimmickMap : Map 'col,row' -> 'ice'|'swamp', or null. Ice folds the
 *                    forced slide into each neighbor expansion (a slide is
 *                    part of the same step); swamp never affects
 *                    connectivity (delay only) and is ignored here.
 */

import { stepBlockedByWall } from './walls.js';
import { resolveSlide } from './gimmicks.js';

const DIRS = [
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
];

/**
 * BFS distance (number of moves) from `start` to any cell whose
 * row === goalRow, moving orthogonally and respecting walls (plus the
 * optional Phase 2 context above). Returns -1 if unreachable.
 * With gimmicks, one "move" may cover several cells (an ice slide).
 */
export function bfsDistanceToRow(boardSize, wallSet, start, goalRow, ctx = null) {
  if (start.row === goalRow) return 0;

  const maxJumps = ctx && ctx.wallJumps ? ctx.wallJumps : 0;
  const gimmickMap = ctx ? ctx.gimmickMap : null;

  // visited flags indexed by (col * boardSize + row) * layers + jumpsLeft
  const layers = maxJumps + 1;
  const visited = new Uint8Array(boardSize * boardSize * layers);
  const idxOf = (col, row, jumps) => (col * boardSize + row) * layers + jumps;
  visited[idxOf(start.col, start.row, maxJumps)] = 1;

  let frontier = [{ col: start.col, row: start.row, jumps: maxJumps }];
  let dist = 0;

  while (frontier.length > 0) {
    dist += 1;
    const next = [];
    for (const cell of frontier) {
      for (const { dc, dr } of DIRS) {
        let col = cell.col + dc;
        let row = cell.row + dr;
        if (col < 0 || row < 0 || col >= boardSize || row >= boardSize) continue;

        let jumps = cell.jumps;
        if (stepBlockedByWall(wallSet, cell, { col, row })) {
          if (jumps === 0) continue;
          jumps -= 1; // spend one wall crossing (ninja wallJump)
        }

        if (gimmickMap) {
          // Forced ice slide is part of this same move (pawns ignored here).
          const final = resolveSlide(
            boardSize,
            wallSet,
            gimmickMap,
            null,
            { col, row },
            dc,
            dr
          );
          col = final.col;
          row = final.row;
        }

        if (row === goalRow) return dist;
        const idx = idxOf(col, row, jumps);
        if (visited[idx]) continue;
        visited[idx] = 1;
        next.push({ col, row, jumps });
      }
    }
    frontier = next;
  }
  return -1;
}
